import {Inject, Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {LoadingService} from './loading.service';
import {Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js'

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {

    constructor(
        private loadingService: LoadingService,
        private router: Router,
        public toasterService: ToastrService,
        @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth
    ) {
    }

    getErrorMessage(error): { message: string, details?: string } {
        let message = null;
        let details = null;

        if (error?.error && typeof error.error === 'object') {
            if (typeof error.error.message === 'string') {
                const regex = /\[.*?\]:\s*(\[.*\])/;
                const match = error.error.message.match(regex);
                if (match) {
                    try {
                        const parsed = JSON.parse(match[1]);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            message = parsed[0].message;
                            details = parsed[0].details;
                        }
                    } catch {}
                } else {
                    message = error.error.message;
                }
            }
            if ('details' in error.error) details = error.error.details;
        }

        if (!message && typeof error?.error === 'string') {
            try {
                const parsed = JSON.parse(error.error);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    message = parsed[0].message;
                    details = parsed[0].details;
                }
            } catch {}
        }

        if (!message && typeof error?.message === 'string') {
            const msgMatch = error.message.match(/"message"\s*:\s*"([^"]+)"/);
            const detMatch = error.message.match(/"details"\s*:\s*"([^"]+)"/);
            if (msgMatch) message = msgMatch[1];
            if (detMatch) details = detMatch[1];
        }

        if (!message) {
            switch (error.status) {
                case 401: message = 'Unauthorized'; break;
                case 403: message = 'Forbidden'; break;
                case 404: message = 'Not Found'; break;
                default: message = 'unknown error';
            }
        }

        return { message, details };
    }


    handleError(error, request) {
        const parsed = this.getErrorMessage(error);
        const now = new Date();
        const dateStr = now.toLocaleString(); 
        const contentHtml = `
                <div style="text-align:left; font-size:14px;">
                  <strong>URL:</strong> ${request.url} <br>
                  <strong>Status:</strong> ${error.status} <br>
                  <strong>Message:</strong> ${parsed.message} <br>
                  ${parsed.details ? `<strong>Details:</strong> ${parsed.details} <br>` : ''}
                 <strong>Date:</strong> ${dateStr} <br>
                </div>
                `;

        if (error.url?.includes('/apiOrganisation/target/employee/email/')) {
            this.loadingService.setLoading(false, request.url);
            return parsed.message;
        }

        this.loadingService.clearLoading();

        if (this.router.url.startsWith('/admin')) {
            // Admin screens handle and display their own error toasts inline; don't
            // show a duplicate global toast and don't navigate away from the page.
            return throwError(() => error);
        }

        this.toasterService.error(
            contentHtml,
            'Une erreur est survenue; veuillez reprendre la saisie via eContrat',
            {
                enableHtml: true,
                closeButton: true,
                tapToDismiss: false,
                timeOut: 0,
                extendedTimeOut: 0,
                disableTimeOut: true,
                positionClass: 'toast-center-center',
                toastClass: 'ngx-toastr custom-error-toast'
            }
        );

        if (this.router.url !== '/') {
            this.router.navigate(['/opportunities']);
        }

        return parsed.message;
    }


    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.loadingService.setLoading(true, request.url);

        return next.handle(this.addAuthHeaderToAllowedOrigins(request))
            .pipe(catchError((err) => {
                    return this.handleError(err, request);
                }),
                map<HttpEvent<any>, any>((evt: HttpEvent<any>) => {
                    if (evt instanceof HttpResponse) {
                        this.loadingService.setLoading(false, request.url);
                    }
                    return evt;
                }));
    }
    private addAuthHeaderToAllowedOrigins(request: HttpRequest<unknown>): HttpRequest<unknown> {

        const authToken = this._oktaAuth.getAccessToken();
        const req = request.clone({ setHeaders: { 'Authorization': `Bearer ${authToken}` } });
        return req;
    }
}

